import { useState } from "react";
import { FileDown, Lock, Paperclip, Trash2, Upload } from "lucide-react";
import { ApiError, downloadFile } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button, Dialog, Field, Input, Select } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { attachmentUrl } from "./api";
import {
  ACTIONS_NEEDING_REASON,
  ACTION_BUTTONS,
  ACTION_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  priorityStyle,
  statusTone,
} from "./labels";
import {
  useAddNote,
  useDeleteTicket,
  usePerformAction,
  useSetTicketTags,
  useTicket,
  useTransferSite,
  useUploadAttachment,
  useDeleteAttachment,
} from "./hooks";
import { useSites } from "@/features/sites/hooks";
import { slaBadge } from "./sla";
import { ChatPanel } from "@/features/chat/ChatPanel";
import { TicketAssetsPanel } from "./TicketAssetsPanel";
import { TicketAiPanel } from "@/features/ai/TicketAiPanel";
import { TicketWhatsappPanel } from "@/features/whatsapp/TicketWhatsappPanel";
import { useServiceReport } from "@/features/engineer/hooks";
import { serviceReportPdfUrl } from "@/features/engineer/api";

interface Props {
  ticketId: string | null;
  onClose: () => void;
  engineers: { id: string; full_name: string }[];
}

export function TicketDetailDialog({ ticketId, onClose, engineers }: Props) {
  const { can, user } = useAuth();
  const { data: ticket, isLoading } = useTicket(ticketId);
  const act = usePerformAction();
  const addNote = useAddNote();
  const upload = useUploadAttachment();
  const deleteAttachment = useDeleteAttachment();
  const del = useDeleteTicket();
  // A signed/unsigned service report may exist for a resolved visit; surface
  // it so customers can download their report (brief: "Download Reports").
  const { data: report } = useServiceReport(ticketId);

  const transfer = useTransferSite();
  const canTransfer = can("ticket:transfer:site");
  const { data: sites } = useSites({ enabled: canTransfer && ticketId !== null });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferSiteId, setTransferSiteId] = useState("");
  const [transferAssignee, setTransferAssignee] = useState("");
  const [transferReason, setTransferReason] = useState("");

  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [noteBody, setNoteBody] = useState("");
  const [noteInternal, setNoteInternal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const canWriteInternal = can("ticket:note:internal");
  // Deleting a ticket is a super-admin (administrator) privilege only.
  const isAdmin = user?.role_code === "admin";

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
    } catch (err) {
      // Workflow refusals (wrong state, wrong role, missing reason) land here.
      setError(err instanceof ApiError ? err.message : "Something went wrong.");
    }
  }

  function startAction(action: string) {
    setError(null);
    setReason("");
    setAssigneeId("");
    if (action === "assign" || ACTIONS_NEEDING_REASON.has(action)) {
      setPendingAction(action);
    } else {
      void run(async () => {
        await act.mutateAsync({ id: ticket!.id, body: { action } });
      });
    }
  }

  async function handleDelete() {
    if (!ticket) return;
    await run(async () => {
      await del.mutateAsync(ticket.id);
      onClose();
    });
  }

  async function confirmAction() {
    if (!ticket || !pendingAction) return;
    await run(async () => {
      await act.mutateAsync({
        id: ticket.id,
        body: {
          action: pendingAction,
          reason: reason.trim() || null,
          assignee_id: pendingAction === "assign" ? assigneeId || null : null,
        },
      });
      setPendingAction(null);
      setReason("");
    });
  }

  async function submitTransfer() {
    if (!ticket || !transferSiteId) return;
    setError(null);
    try {
      await transfer.mutateAsync({
        id: ticket.id,
        body: {
          site_id: transferSiteId,
          assignee_id: transferAssignee || null,
          reason: transferReason.trim() || null,
        },
      });
      setTransferOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not move the ticket.");
    }
  }

  return (
    <>
    <Dialog
      open={ticketId !== null}
      onClose={onClose}
      size="xl"
      title={ticket ? `${ticket.ticket_number} · ${ticket.title}` : "Ticket"}
    >
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {ticket && (
        <div className="space-y-5 text-sm">
          {error && (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-destructive"
            >
              {error}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(ticket.status)}>{STATUS_LABELS[ticket.status]}</Badge>
            <span
              className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
              style={priorityStyle(ticket.priority)}
            >
              {PRIORITY_LABELS[ticket.priority]}
            </span>
            {ticket.reopened_count > 0 && (
              <Badge tone="warning">Reopened ×{ticket.reopened_count}</Badge>
            )}
          </div>

          {/* Tags are internal metadata — staff only, never shown in the portal. */}
          {!user?.customer_id && (
            <TagsSection ticketId={ticket.id} tags={ticket.tags} canEdit={can("ticket:update")} />
          )}

          <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3">
            <SlaClock
              label="First response"
              state={ticket.sla.response_state}
              dueAt={ticket.sla.response_due_at}
            />
            <SlaClock
              label="Resolution"
              state={ticket.sla.resolution_state}
              dueAt={ticket.sla.resolution_due_at}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Detail label="Customer" value={ticket.customer_name} />
            <Detail label="Site" value={ticket.site_name ?? "—"} />
            <Detail label="Office" value={ticket.location_name ?? "—"} />
            <Detail label="Category" value={ticket.category_name ?? "—"} />
            <Detail
              label="Engineer"
              value={ticket.assigned_engineer_name ?? "Unassigned"}
            />
            <Detail label="Raised by" value={ticket.created_by_name} />
            {ticket.reporter_name && (
              <Detail
                label="Reported by"
                value={[ticket.reporter_name, ticket.reporter_phone, ticket.reporter_email]
                  .filter(Boolean)
                  .join(" · ")}
              />
            )}
          </div>

          <section>
            <h3 className="mb-1 font-medium">Description</h3>
            <p className="whitespace-pre-wrap text-muted-foreground">{ticket.description}</p>
          </section>

          {/* Advisory AI triage, similar tickets, KB and engineer suggestions. */}
          <TicketAiPanel ticketId={ticket.id} />

          {/* Assets this ticket concerns, from the customer's inventory. */}
          <TicketAssetsPanel ticketId={ticket.id} customerId={ticket.customer_id} />

          {/* WhatsApp conversation on this ticket (window-aware sending). */}
          <TicketWhatsappPanel ticketId={ticket.id} />

          {/* Live chat between the customer and the team, on this ticket. */}
          <ChatPanel ticketId={ticket.id} />

          {/* Actions come from the server; it re-checks each one on submit. */}
          {ticket.available_actions.length > 0 && (
            <section>
              <h3 className="mb-2 font-medium">Actions</h3>
              <div className="flex flex-wrap gap-2">
                {ticket.available_actions.map((action) => (
                  <Button
                    key={action}
                    variant={action === "cancel" ? "destructive" : "outline"}
                    disabled={act.isPending}
                    onClick={() => startAction(action)}
                  >
                    {ACTION_BUTTONS[action] ?? action}
                  </Button>
                ))}
              </div>
            </section>
          )}

          {/* Cross-site handoff — Super Admin only. */}
          {canTransfer && (
            <section className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium">Move to another site</h3>
                  <p className="text-xs text-muted-foreground">
                    Hand this ticket to a different site’s admin or engineer.
                    Currently at <span className="font-medium">{ticket.site_name ?? "—"}</span>.
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    setTransferSiteId("");
                    setTransferAssignee("");
                    setTransferReason("");
                    setError(null);
                    setTransferOpen(true);
                  }}
                >
                  Move to site…
                </Button>
              </div>
            </section>
          )}

          {/* Delete is destructive and super-admin only. */}
          {isAdmin && (
            <section className="rounded-md border border-destructive/40 p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-medium text-destructive">Delete ticket</h3>
                  <p className="text-xs text-muted-foreground">
                    Removes this ticket from all views. This can't be undone from the app.
                  </p>
                </div>
                {confirmingDelete ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                      Delete {ticket.ticket_number}?
                    </span>
                    <Button
                      variant="destructive"
                      disabled={del.isPending}
                      onClick={() => void handleDelete()}
                    >
                      {del.isPending ? "Deleting…" : "Yes, delete"}
                    </Button>
                    <Button variant="ghost" onClick={() => setConfirmingDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button variant="destructive" onClick={() => setConfirmingDelete(true)}>
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    Delete
                  </Button>
                )}
              </div>
            </section>
          )}

          {pendingAction && (
            <section className="space-y-3 rounded-md border border-border p-3">
              <h4 className="font-medium">{ACTION_BUTTONS[pendingAction] ?? pendingAction}</h4>
              {pendingAction === "assign" && (
                <Field label="Assign to" htmlFor="assignee">
                  <Select
                    id="assignee"
                    value={assigneeId}
                    onChange={(e) => setAssigneeId(e.target.value)}
                  >
                    <option value="">Select a staff member…</option>
                    {engineers.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.full_name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {ACTIONS_NEEDING_REASON.has(pendingAction) && (
                <Field label="Reason (required)" htmlFor="reason">
                  <Input
                    id="reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </Field>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setPendingAction(null)}>
                  Cancel
                </Button>
                <Button
                  disabled={
                    act.isPending ||
                    (pendingAction === "assign" && !assigneeId) ||
                    (ACTIONS_NEEDING_REASON.has(pendingAction) && !reason.trim())
                  }
                  onClick={() => void confirmAction()}
                >
                  Confirm
                </Button>
              </div>
            </section>
          )}

          <section>
            <h3 className="mb-2 font-medium">Notes ({ticket.notes.length})</h3>
            <ul className="space-y-2">
              {ticket.notes.length === 0 && (
                <li className="text-muted-foreground">No notes yet.</li>
              )}
              {ticket.notes.map((note) => (
                <li
                  key={note.id}
                  className="rounded-md border border-border p-2"
                  data-visibility={note.visibility}
                >
                  <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{note.author_name}</span>
                    <span>·</span>
                    <span>{formatDateTime(note.created_at)}</span>
                    {note.visibility === "internal" && (
                      <span className="inline-flex items-center gap-1 text-sla-warn">
                        <Lock className="h-3 w-3" aria-hidden="true" />
                        Internal
                      </span>
                    )}
                  </div>
                  <p className="whitespace-pre-wrap">{note.body}</p>
                </li>
              ))}
            </ul>

            <div className="mt-3 space-y-2">
              <Input
                aria-label="New note"
                placeholder="Add a note"
                value={noteBody}
                onChange={(e) => setNoteBody(e.target.value)}
              />
              <div className="flex items-center justify-between gap-2">
                {canWriteInternal ? (
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={noteInternal}
                      onChange={(e) => setNoteInternal(e.target.checked)}
                    />
                    Internal only (never shown to the customer)
                  </label>
                ) : (
                  <span />
                )}
                <Button
                  variant="outline"
                  disabled={!noteBody.trim() || addNote.isPending}
                  onClick={() =>
                    void run(async () => {
                      await addNote.mutateAsync({
                        id: ticket.id,
                        body: {
                          body: noteBody.trim(),
                          visibility: noteInternal ? "internal" : "public",
                        },
                      });
                      setNoteBody("");
                    })
                  }
                >
                  Add note
                </Button>
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-2 font-medium">Attachments ({ticket.attachments.length})</h3>
            <ul className="space-y-1">
              {ticket.attachments.length === 0 && (
                <li className="text-xs text-muted-foreground">No files attached.</li>
              )}
              {ticket.attachments.map((a) => (
                <li key={a.id} className="flex items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                  {/* Not a plain <a download>: the bearer token lives in
                      memory, so the browser cannot attach it and the request
                      401s. The server still forces Content-Disposition:
                      attachment, so nothing renders inline. */}
                  <button
                    type="button"
                    className="text-left hover:underline"
                    onClick={() =>
                      void run(() => downloadFile(attachmentUrl(a.id), a.file_name))
                    }
                  >
                    {a.file_name}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {(a.size_bytes / 1024).toFixed(0)} KB
                  </span>
                  {a.visibility === "internal" && <Badge tone="warning">Internal</Badge>}
                  <button
                    type="button"
                    aria-label={`Delete ${a.file_name}`}
                    title="Delete file"
                    className="ml-auto rounded p-1 text-muted-foreground hover:text-destructive"
                    onClick={() => {
                      if (window.confirm(`Delete "${a.file_name}"? This can't be undone.`)) {
                        void run(() => deleteAttachment.mutateAsync(a.id));
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            {/* Anyone who can see the ticket can attach to it — customers
                included (the whole point of the portal's "upload files"). The
                server validates magic bytes and forces public visibility for
                customers, so no client trust is required here. */}
            <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 text-sm text-primary hover:underline">
              <Upload className="h-4 w-4" aria-hidden="true" />
              {upload.isPending ? "Uploading…" : "Attach a file"}
              <input
                type="file"
                className="sr-only"
                disabled={upload.isPending}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    void run(() => upload.mutateAsync({ id: ticket.id, file }));
                  }
                  e.target.value = ""; // allow re-selecting the same file
                }}
              />
            </label>
          </section>

          {report && (
            <section>
              <h3 className="mb-2 font-medium">Service report</h3>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{report.report_number}</span>
                <Badge tone={report.is_signed ? "success" : "warning"}>
                  {report.is_signed ? `Signed by ${report.signed_by_name}` : "Unsigned"}
                </Badge>
                <Button
                  variant="outline"
                  onClick={() =>
                    void run(() =>
                      downloadFile(
                        serviceReportPdfUrl(report.id),
                        `${report.report_number}.pdf`,
                      ),
                    )
                  }
                >
                  <FileDown className="h-4 w-4" aria-hidden="true" />
                  Download PDF
                </Button>
              </div>
            </section>
          )}

          <section>
            <h3 className="mb-2 font-medium">Timeline</h3>
            <ol className="space-y-1.5">
              {ticket.events.map((e) => (
                <li key={e.id} className="text-xs text-muted-foreground">
                  <span className="text-foreground">{e.actor_name ?? "System"}</span>{" "}
                  {ACTION_LABELS[e.event_type] ?? e.event_type}
                  {e.detail ? <span> {e.detail}</span> : null}
                  {e.reason ? <span> — “{e.reason}”</span> : null}
                  <span> · {formatDateTime(e.created_at)}</span>
                </li>
              ))}
            </ol>
          </section>
        </div>
      )}
    </Dialog>

    <Dialog
      open={transferOpen}
      onClose={() => setTransferOpen(false)}
      title="Move ticket to another site"
    >
      {error && (
        <div role="alert" className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="space-y-4">
        <Field label="Target site" htmlFor="transfer_site">
          <Select
            id="transfer_site"
            value={transferSiteId}
            onChange={(e) => setTransferSiteId(e.target.value)}
          >
            <option value="">Select a site…</option>
            {(sites ?? [])
              .filter((s) => s.id !== ticket?.site_id)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </Select>
        </Field>
        <Field
          label="Assign to (optional — must be on the target site)"
          htmlFor="transfer_assignee"
        >
          <Select
            id="transfer_assignee"
            value={transferAssignee}
            onChange={(e) => setTransferAssignee(e.target.value)}
          >
            <option value="">Leave for the site to dispatch</option>
            {engineers.map((e) => (
              <option key={e.id} value={e.id}>
                {e.full_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Reason (optional)" htmlFor="transfer_reason">
          <Input
            id="transfer_reason"
            value={transferReason}
            onChange={(e) => setTransferReason(e.target.value)}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setTransferOpen(false)}>
            Cancel
          </Button>
          <Button disabled={!transferSiteId || transfer.isPending} onClick={() => void submitTransfer()}>
            Move ticket
          </Button>
        </div>
      </div>
    </Dialog>
    </>
  );
}

function SlaClock({
  label,
  state,
  dueAt,
}: {
  label: string;
  state: Parameters<typeof slaBadge>[0];
  dueAt: string | null;
}) {
  const badge = slaBadge(state, dueAt);
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5">
        <Badge tone={badge.tone}>{badge.label}</Badge>
      </div>
    </div>
  );
}

function TagsSection({
  ticketId,
  tags,
  canEdit,
}: {
  ticketId: string;
  tags: string[];
  canEdit: boolean;
}) {
  const setTags = useSetTicketTags();
  const [input, setInput] = useState("");

  function apply(next: string[]) {
    void setTags.mutateAsync({ id: ticketId, tags: next });
  }
  function addFromInput() {
    const t = input.trim().toLowerCase();
    setInput("");
    if (t && !tags.includes(t)) apply([...tags, t]);
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Tags:</span>
      {tags.length === 0 && <span className="text-xs text-muted-foreground">none</span>}
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 rounded bg-accent px-1.5 py-0.5 text-xs text-accent-foreground"
        >
          {tag}
          {canEdit && (
            <button
              type="button"
              aria-label={`Remove tag ${tag}`}
              className="text-muted-foreground hover:text-destructive"
              onClick={() => apply(tags.filter((x) => x !== tag))}
            >
              ×
            </button>
          )}
        </span>
      ))}
      {canEdit && (
        <input
          aria-label="Add tag"
          placeholder="add tag…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addFromInput();
            }
          }}
          className="h-6 w-24 rounded border border-input bg-background px-1.5 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      )}
    </div>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div>{value}</div>
    </div>
  );
}
